import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import PersonAddRoundedIcon from '@mui/icons-material/PersonAddRounded';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import TelegramIcon from '@mui/icons-material/Telegram';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import WindowRoundedIcon from '@mui/icons-material/WindowRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import AndroidRoundedIcon from '@mui/icons-material/AndroidRounded';
import AppleIcon from '@mui/icons-material/Apple';
import TvRoundedIcon from '@mui/icons-material/TvRounded';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import MoreTimeRoundedIcon from '@mui/icons-material/MoreTimeRounded';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import GroupRoundedIcon from '@mui/icons-material/GroupRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import UserPanelHomeShowcases, { APP_PLATFORMS } from './UserPanelHomeShowcases';
import UserPanelMovies from './UserPanelMovies';
import UserPanelReferrals from './UserPanelReferrals';
import UserPanelSearch from './UserPanelSearch';
import UserPanelSeries from './UserPanelSeries';
import UserPanelSetup from './UserPanelSetup';
import UserPanelSetupDetail from './UserPanelSetupDetail';
import UserPanelSubUsers from './UserPanelSubUsers';
import UserPanelSupport from './UserPanelSupport';
import UserPanelTableLoading from './UserPanelTableLoading';
import UserPanelWebTV from './UserPanelWebTV';
import UserPanelCreditModal from './UserPanelCreditModal';
import UserPanelDurationModal from './UserPanelDurationModal';
import UserPanelAccountModal from './UserPanelAccountModal';
import UserPanelAdultModal from './UserPanelAdultModal';
import UserPanelCategoryModal from './UserPanelCategoryModal';
import UserPanelCommentsModal from './UserPanelCommentsModal';
import UserPanelMovieDetailModal from './UserPanelMovieDetailModal';
import UserPanelManageModal from './UserPanelManageModal';
import UserPanelNotifications from './UserPanelNotifications';
import UserPanelReviewModal from './UserPanelReviewModal';
import UserPanelSecurityModal from './UserPanelSecurityModal';
import UserPanelTransactionHistoryModal from './UserPanelTransactionHistoryModal';
import UserPanelWindowsDownloadModal from './UserPanelWindowsDownloadModal';
import { formatCreditAmount, getCreditCurrency } from './creditCurrency';
import { detectUserDevicePlatform } from './userPanelDevicePlatform';
import { ADMIN_SECTIONS, ADMIN_SECTION_IDS } from './userPanelAdminData';
import {
  getLegacyCreditIdentity,
  loadLegacyCreditSettings,
  resolveLegacyCreditFlow,
  resolveLegacyCreditModalSetting,
} from './creditFlowResolver';
import {
  buildLegacyCreditWhatsappUrl,
  LEGACY_PAYMENT_ORIGINS,
  recordLegacySupheroCreditHistory,
  startLegacyCreditCheckout,
} from './creditProviderGateway';
import {
  getTivigoCryptoCreditCheckoutStatus,
  shouldRecordLegacyCreditResult,
  startTivigoCryptoCreditCheckout as startTivigoCryptoCheckout,
} from './creditCheckoutApi';
import {
  openSupheroChat,
  TELEGRAM_SUPPORT_URL,
  WHATSAPP_SUPPORT_URL,
} from './supportChannels';
import {
  canRestrictedAccountNavigate,
  canRestrictedAccountOpenRoute,
  getAccountAccessState,
  hasExpiredResellerAccess,
  RESTRICTED_ACTION_MESSAGE,
} from './accountAccess';
import './userPanelTemplate.css';
import './userPanelSearch.css';

const UserPanelAdmin = React.lazy(() => import('./UserPanelAdmin'));

const iconPath = (name) => `url("/assets/icons/lucide/${name}.svg")`;
const ACCOUNT_TAB_ORDER = ['login', 'package'];
const ONE_HOUR_MS = 60 * 60 * 1000;
const ONE_DAY_MS = 24 * ONE_HOUR_MS;

const readStoredAccountCredentials = () => {
  if (typeof window === 'undefined') return {};

  try {
    const credentials = JSON.parse(window.localStorage.getItem('xtream_credentials') || '{}');
    return {
      username: typeof credentials?.username === 'string' ? credentials.username : '',
      password: typeof credentials?.password === 'string' ? credentials.password : '',
    };
  } catch (error) {
    return {};
  }
};

const normalizeAccountServerUrl = (value) => {
  const serverUrl = String(value || '').trim();
  if (!serverUrl) return 'http://tivigo.store';

  return /^https:\/\/tivigo\.store\/?$/i.test(serverUrl)
    ? 'http://tivigo.store'
    : serverUrl;
};

const formatRemainingTimeLabel = (millisecondsLeft) => {
  if (millisecondsLeft < ONE_DAY_MS) {
    return `${Math.ceil(millisecondsLeft / ONE_HOUR_MS).toLocaleString('tr-TR')} Saat`;
  }

  return `${Math.ceil(millisecondsLeft / ONE_DAY_MS).toLocaleString('tr-TR')} Gün`;
};

const getTimeBasedGreeting = (hour, username) => {
  const greetingName = username
    ? `${username.charAt(0).toLocaleUpperCase('tr-TR')}${username.slice(1)}`
    : 'Tivigo Kullanıcısı';

  if (hour >= 5 && hour < 12) return `Günaydın ${greetingName}, Hoşgeldin!`;
  if (hour >= 12 && hour < 18) return `İyi Günler ${greetingName}, Hoşgeldin!`;
  if (hour >= 18 && hour < 22) return `İyi Akşamlar ${greetingName}, Hoşgeldin!`;
  return `İyi Geceler ${greetingName}, Hoşgeldin! 🌙`;
};

const navigationItems = [
  { id: 'overview', label: 'Anasayfa', icon: 'monitor-play' },
  { id: 'setup', label: 'Kurulum Yap', icon: 'cloud-download' },
  { id: 'live', label: 'Web TV', icon: 'radio-tower' },
  { id: 'movies', label: 'Filmler', icon: 'clapperboard' },
  { id: 'series', label: 'Diziler', icon: 'monitor-play' },
  { id: 'user-actions', label: 'Alt Kullanıcılarım', icon: 'users-round' },
  { id: 'credit', label: 'Kredi Yükle', icon: 'wallet-cards' },
  { id: 'extend', label: 'Süre Ekle', icon: 'package-check' },
  { id: 'support', label: 'Destek', icon: 'headphones' },
  { id: 'bonus', label: '250 TL Bonus Kazan', icon: 'user-plus' },
];

const overviewCards = [
  { label: 'Kredi', value: '—', icon: 'circle-dollar-sign', tone: 'orange', action: 'credit' },
  { label: 'Bitiş Tarihi', value: '—', icon: 'package-check', tone: 'violet', action: 'extend' },
  { label: 'Alt Kullanıcılar', value: '—', icon: 'users-round', tone: 'cyan', action: 'user-actions', resellerOnly: true },
  { label: 'Davet Edilenler', value: '—', icon: 'user-plus', tone: 'green', action: 'bonus' },
];

const quickActions = [
  { label: 'Kredi Yükle', note: 'Hesabına güvenli şekilde kredi ekle', kicker: 'KREDİ', icon: AccountBalanceWalletOutlinedIcon, tone: 'orange', variant: 'utility', action: 'credit' },
  { label: 'Süre Ekle / Uzat', note: 'Mevcut paketine süre ekle', kicker: 'ÜYELİK', icon: MoreTimeRoundedIcon, tone: 'violet', variant: 'utility', action: 'extend' },
  { label: 'Kategorileri Yönet', note: 'İçerik tercihlerini düzenle', kicker: 'TERCİHLER', icon: CategoryOutlinedIcon, tone: 'cyan', variant: 'utility', action: 'categories' },
  { label: 'Fiyatlar', note: 'Güncel paketleri karşılaştır', kicker: 'PAKETLER', icon: SellOutlinedIcon, tone: 'yellow', variant: 'utility', action: 'extend' },
  { label: 'Android', note: 'Telefon ve TV', kicker: 'UYGULAMA', icon: AndroidRoundedIcon, tone: 'green', variant: 'platform', action: 'setup', setupDetailId: 'tivigo' },
  { label: 'iOS', note: 'iPhone ve iPad', kicker: 'UYGULAMA', icon: AppleIcon, tone: 'violet', variant: 'platform', action: 'setup', setupDetailId: 'tivigo-ios' },
  { label: 'Windows', note: 'Windows 10 / 11', kicker: 'MASAÜSTÜ', icon: WindowRoundedIcon, tone: 'cyan', variant: 'platform', action: 'setup', setupDetailId: 'tivigo-pc' },
  { label: 'LG webOS', note: 'LG Smart TV', kicker: 'SMART TV', icon: TvRoundedIcon, tone: 'red', variant: 'platform', action: 'setup', setupDetailId: 'tivigo-lg-webos' },
];

const DASHBOARD_HERO_COPY = {
  windows: {
    headlinePrefix: 'Türkiye’de bir ilk olan',
    headlineAccent: 'Tivigo Windows',
    headlineSuffix: 'Uygulaması yayınlandı!',
    description: 'Hesabınızı bilgisayardan yönetmek ve içerikleri izlemek için hemen indirin.',
    cta: 'Windows için indir',
    metaPrimary: 'Windows 10 / 11',
    metaSecondary: 'v0.1.36 · 64-bit',
    previewVersion: 'v0.1.36',
  },
  android: {
    headlinePrefix: '',
    headlineAccent: 'Tivigo Android',
    headlineSuffix: 'ile her şey tek dokunuşta.',
    description: 'Hesabınızı Android telefon veya TV’den yönetin ve içeriklerinizi Tivigo uygulamasında izleyin.',
    cta: 'Android için indir',
    metaPrimary: 'Android telefon ve TV',
    metaSecondary: 'Google Play',
    previewVersion: 'Google Play',
  },
  ios: {
    headlinePrefix: '',
    headlineAccent: 'Tivigo iOS',
    headlineSuffix: 'ile her an, her yerde.',
    description: 'Hesabınızı iPhone veya iPad’den yönetin ve içeriklerinizi Tivigo iOS deneyimiyle izleyin.',
    cta: 'iOS için indir',
    metaPrimary: 'iPhone ve iPad',
    metaSecondary: 'App Store',
    previewVersion: 'App Store',
  },
  macos: {
    headlinePrefix: '',
    headlineAccent: 'Tivigo macOS',
    headlineSuffix: 'ile masaüstünde daha fazlası.',
    description: 'Hesabınızı Mac’inizden yönetin ve içeriklerinizi Tivigo macOS uygulamasında izleyin.',
    cta: 'macOS için indir',
    metaPrimary: 'Mac',
    metaSecondary: 'DMG',
    previewVersion: 'DMG',
  },
};

const resolveDashboardHeroPlatform = () => {
  const platformId = detectUserDevicePlatform();
  const platform = APP_PLATFORMS.find((item) => item.id === platformId) || APP_PLATFORMS.find((item) => item.id === 'windows');
  return { ...platform, ...(DASHBOARD_HERO_COPY[platformId] || DASHBOARD_HERO_COPY.windows) };
};

const MaskIcon = ({ name, className = '' }) => (
  <span
    className={`tvup-mask-icon ${className}`.trim()}
    style={{ '--tvup-icon': iconPath(name) }}
    aria-hidden="true"
  />
);

function UserPanelTemplate({
  userInfo,
  onLogout,
  initialActiveItem = 'overview',
  initialSetupTargetUser = null,
  initialSetupDevice = null,
  initialSetupSourceAppId = null,
  initialSeriesPlatform = null,
  initialAdminFilter = null,
  onNavigateSection,
  onNavigateSetupDetail,
  onNavigateToPage,
  onStartCreditCheckout,
  onStartCryptoCreditCheckout,
  onVerifyCreditPayment,
  onCreditPaymentResult,
  creditProvider,
  creditPaymentOrigins,
  searchPreview = false,
  creditPreview = false,
  durationPreview = false,
  adminPreview = false,
  onPurchaseDuration,
  onValidateDurationCoupon,
  onDurationSuccess,
  onSubmitReview,
  movies = [],
  moviesLoading = false,
  series = [],
  seriesLoading = false,
  liveTv = null,
  liveTvLoading = false,
  subUsersData = null,
  subUsersLoading = false,
  supportTickets = [],
  supportLoading = false,
  supportUnreadCount = null,
  referrals = [],
  referralCode = '',
  referralsLoading = false,
  onCreateSubUser,
  onRemoveSubUser,
  onCreateTicket,
  onReplyTicket,
  onEditTicketReply,
  onSetTicketStatus,
  onClaimReferralBonus,
  onLoadAccount,
  onUpdateProfile,
  onChangePassword,
  onUpdateEmail,
  onSendEmailCode,
  onSendPhoneCode,
  onUpdatePhone,
  onLoadTrustedDevices,
  onLoadLoginHistory,
  onReportLogin,
  onRemoveTrustedDevice,
  onLoadBouquets,
  onUpdateBouquets,
  onFetchSecurityLogs,
  onFetchTransactionHistory,
  onLoadMovieDetail,
  onLoadSeriesDetail,
  onLoadComments,
  onToggleCommentHelpful,
  onLoadTicket,
  onLoadResellerTestLimit,
  isReseller = false,
  resellerInfo = null,
}) {
  const [activeItem, setActiveItem] = useState(initialActiveItem);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const [creditModalOpen, setCreditModalOpen] = useState(false);
  const [creditFlow, setCreditFlow] = useState(null);
  const [creditSettingsCache, setCreditSettingsCache] = useState(null);
  const [durationModalOpen, setDurationModalOpen] = useState(false);
  const [accountModalOpen, setAccountModalOpen] = useState(false);
  const [accountModalInitialTab, setAccountModalInitialTab] = useState('personal');
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [adultModalOpen, setAdultModalOpen] = useState(false);
  const [securityModalOpen, setSecurityModalOpen] = useState(false);
  const [transactionHistoryModalOpen, setTransactionHistoryModalOpen] = useState(false);
  const [windowsDownloadModalOpen, setWindowsDownloadModalOpen] = useState(false);
  const [selectedWindowsContent, setSelectedWindowsContent] = useState(null);
  const [windowsDownloadContentType, setWindowsDownloadContentType] = useState(null);
  const [movieDetailDialog, setMovieDetailDialog] = useState({ open: false, movie: null, contentType: 'movie' });
  const [commentsDialog, setCommentsDialog] = useState({ open: false, content: null, contentType: null });
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [manageModalOpen, setManageModalOpen] = useState(false);
  const [selectedSubUser, setSelectedSubUser] = useState(null);
  const [modalTargetUser, setModalTargetUser] = useState(null);
  const [setupTargetUser, setSetupTargetUser] = useState(initialSetupTargetUser);
  const [setupDevice, setSetupDevice] = useState(initialSetupDevice);
  const [setupSourceAppId, setSetupSourceAppId] = useState(initialSetupSourceAppId);
  const profileMenuRef = useRef(null);
  const adminMenuRef = useRef(null);
  const adminMenuTriggerRef = useRef(null);
  const scrollAreaRef = useRef(null);
  const creditOpenInFlightRef = useRef(false);
  const [headerScrolled, setHeaderScrolled] = useState(false);
  const [showAccountPassword, setShowAccountPassword] = useState(false);
  const [copiedAccountField, setCopiedAccountField] = useState(null);
  const [activeAccountTab, setActiveAccountTab] = useState('login');
  const [currentHour, setCurrentHour] = useState(() => new Date().getHours());
  const dashboardHeroPlatform = useMemo(resolveDashboardHeroPlatform, []);
  const DashboardHeroPlatformIcon = dashboardHeroPlatform.icon;
  const activeAccountTabIndex = Math.max(0, ACCOUNT_TAB_ORDER.indexOf(activeAccountTab));
  const accountTabIndicatorX = ['0px', 'calc(100% + 4px)'][activeAccountTabIndex];
  const routeProgressKey = activeItem === 'setup'
    ? `${activeItem}:${setupDevice || 'index'}`
    : activeItem;
  const handleStartCreditCheckout = useCallback((checkoutRequest) => {
    if (typeof onStartCreditCheckout === 'function') {
      return onStartCreditCheckout({ ...checkoutRequest, flow: creditFlow });
    }

    return startLegacyCreditCheckout({
      ...checkoutRequest,
      flow: creditFlow,
    });
  }, [creditFlow, onStartCreditCheckout]);
  const handleStartCryptoCreditCheckout = useCallback((checkoutRequest) => {
    if (typeof onStartCryptoCreditCheckout === 'function') {
      return onStartCryptoCreditCheckout({ ...checkoutRequest, userInfo });
    }

    return startTivigoCryptoCheckout({
      ...checkoutRequest,
      userInfo,
    });
  }, [onStartCryptoCreditCheckout, userInfo]);
  const handleGetCryptoCreditCheckoutStatus = useCallback((checkoutRequest) => (
    getTivigoCryptoCreditCheckoutStatus({
      ...checkoutRequest,
      userInfo,
    })
  ), [userInfo]);
  const handleCreditPaymentResult = useCallback(async (result) => {
    if (shouldRecordLegacyCreditResult(result)) {
      try {
        await recordLegacySupheroCreditHistory({
          flow: creditFlow,
          amount: result.amount,
          provider: result.provider,
        });
      } catch (error) {
        // Eski akışta işlem geçmişi isteği ödeme sonucunu geri almıyordu.
      }
    }

    if (typeof onCreditPaymentResult === 'function') {
      await onCreditPaymentResult(result);
    }
  }, [creditFlow, onCreditPaymentResult]);

  useEffect(() => {
    if (creditPreview) return undefined;

    let active = true;
    const timer = window.setTimeout(() => {
      loadLegacyCreditSettings()
        .then((settings) => {
          if (active && settings) setCreditSettingsCache(settings);
        })
        .catch(() => {});
    }, 400);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [creditPreview]);

  useLayoutEffect(() => {
    const body = document.body;
    const html = document.documentElement;
    const previousZoom = body.style.zoom;
    const previousBodyOverflow = body.style.overflow;
    const previousHtmlOverflow = html.style.overflow;

    body.style.overflow = 'hidden';
    html.style.overflow = 'hidden';

    const applyDashboardDensity = () => {
      body.style.zoom = window.innerWidth >= 900 ? '0.95' : '1';
    };

    applyDashboardDensity();
    window.addEventListener('resize', applyDashboardDensity);

    return () => {
      window.removeEventListener('resize', applyDashboardDensity);
      body.style.zoom = previousZoom;
      body.style.overflow = previousBodyOverflow;
      html.style.overflow = previousHtmlOverflow;
    };
  }, []);

  useEffect(() => {
    if (!ACCOUNT_TAB_ORDER.includes(activeAccountTab)) {
      setActiveAccountTab('login');
    }
  }, [activeAccountTab]);

  useEffect(() => {
    const updateCurrentHour = () => setCurrentHour(new Date().getHours());
    const interval = window.setInterval(updateCurrentHour, 60000);

    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    const scrollArea = scrollAreaRef.current;
    if (!scrollArea) return undefined;

    const updateHeaderState = () => setHeaderScrolled(scrollArea.scrollTop > 8);

    updateHeaderState();
    scrollArea.addEventListener('scroll', updateHeaderState, { passive: true });

    return () => scrollArea.removeEventListener('scroll', updateHeaderState);
  }, []);

  useEffect(() => {
    const scrollArea = scrollAreaRef.current;
    if (!scrollArea) return;

    scrollArea.scrollTo({ top: 0, behavior: 'auto' });
    setHeaderScrolled(false);
  }, [activeItem, setupDevice]);

  useEffect(() => {
    setActiveItem(initialActiveItem);
  }, [initialActiveItem]);

  useEffect(() => {
    if (initialActiveItem !== 'setup') return;
    setSetupTargetUser(initialSetupTargetUser);
    setSetupDevice(initialSetupDevice);
    setSetupSourceAppId(initialSetupSourceAppId);
  }, [initialActiveItem, initialSetupTargetUser, initialSetupDevice, initialSetupSourceAppId]);

  useEffect(() => {
    if (!profileOpen) return undefined;

    const closeProfileMenu = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setProfileOpen(false);
      }
    };

    document.addEventListener('pointerdown', closeProfileMenu);

    return () => document.removeEventListener('pointerdown', closeProfileMenu);
  }, [profileOpen]);

  useEffect(() => {
    if (!adminMenuOpen) return undefined;

    const closeAdminMenu = (event) => {
      if (event.type === 'keydown' && event.key !== 'Escape') return;
      if (event.type === 'pointerdown' && adminMenuRef.current?.contains(event.target)) return;
      setAdminMenuOpen(false);
      if (event.type === 'keydown') adminMenuTriggerRef.current?.focus();
    };

    document.addEventListener('pointerdown', closeAdminMenu);
    document.addEventListener('keydown', closeAdminMenu);

    return () => {
      document.removeEventListener('pointerdown', closeAdminMenu);
      document.removeEventListener('keydown', closeAdminMenu);
    };
  }, [adminMenuOpen]);

  const user = useMemo(() => {
    const info = userInfo?.user_info || {};
    const username = info.username || 'Tivigo kullanıcısı';
    const displayName = info.ad || username;

    return {
      username,
      displayName,
      initial: displayName.trim().charAt(0).toUpperCase() || 'T',
    };
  }, [userInfo]);

  const timeBasedGreeting = useMemo(
    () => getTimeBasedGreeting(currentHour, user.username),
    [currentHour, user.username]
  );

  const account = useMemo(() => {
    const info = userInfo?.user_info || {};
    const liveUser = userInfo?.user || userInfo?.account_data?.user || {};
    const serverInfo = userInfo?.server_info || userInfo?.account_data?.server_info || {};
    const storedCredentials = readStoredAccountCredentials();
    const access = getAccountAccessState(userInfo);

    const username = liveUser.username || info.username || storedCredentials.username || 'Tivigo kullanıcısı';
    const password = liveUser.password || info.password || storedCredentials.password || '';
    const maxConnections = Math.max(
      1,
      parseInt(liveUser.max_connections ?? info.max_connections, 10) || 1
    );
    const expiryTimestamp = Number(liveUser.exp_date ?? info.exp_date);
    const hasValidExpiry = Number.isFinite(expiryTimestamp) && expiryTimestamp > 0;
    const msLeft = hasValidExpiry ? Math.max(0, expiryTimestamp * 1000 - Date.now()) : 0;
    const expiryLabel = hasValidExpiry
      ? new Intl.DateTimeFormat('tr-TR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }).format(new Date(expiryTimestamp * 1000))
      : 'Bilgi bekleniyor';
    const expiryTimeLabel = hasValidExpiry
      ? new Intl.DateTimeFormat('tr-TR', {
          hour: '2-digit',
          minute: '2-digit',
          hourCycle: 'h23',
        }).format(new Date(expiryTimestamp * 1000))
      : '';
    const remainingTimeLabel = hasValidExpiry
      ? formatRemainingTimeLabel(msLeft)
      : 'Bilgi bekleniyor';

    return {
      username,
      password,
      serverUrl: normalizeAccountServerUrl(serverInfo.url),
      maxConnections,
      packageLabel: `${maxConnections} Kişilik`,
      expiryLabel,
      expiryTimeLabel,
      remainingTimeLabel,
      isActive: access.isActive,
      isRestricted: access.isRestricted,
      restrictionReason: access.reason,
      statusLabel: access.statusLabel,
      statusTone: access.statusTone,
      credit: Number.isFinite(Number(liveUser.kredi ?? info.kredi)) ? Number(liveUser.kredi ?? info.kredi) : null,
      subUserCount: Number.isFinite(Number(liveUser.alt_kullanicilar ?? info.alt_kullanicilar))
        ? Number(liveUser.alt_kullanicilar ?? info.alt_kullanicilar)
        : null,
      invitedCount: Number.isFinite(Number(liveUser.davet_edilenler ?? info.davet_edilenler))
        ? Number(liveUser.davet_edilenler ?? info.davet_edilenler)
        : null,
    };
  }, [userInfo]);

  const { currency: creditCurrency, eurTryRate } = getCreditCurrency(userInfo, isReseller);
  // Süresi dolan bayi: alt kullanıcı işlemleri açık kalır, kendi hesabına ait özellikler kısıtlı kalır.
  const expiredResellerAccess = hasExpiredResellerAccess({ reason: account.restrictionReason }, isReseller);
  const isBlockedFor = (targetUser) => account.isRestricted && !(expiredResellerAccess && targetUser);

  const restrictionNotice = useMemo(() => {
    if (account.restrictionReason === 'banned') {
      return {
        title: 'Hesabınız kısıtlandı',
        description: 'Güvenlik nedeniyle bazı özellikler kapatıldı. Hesabınızın durumunu öğrenmek için WhatsApp üzerinden bize ulaşın.',
        action: 'support',
        actionLabel: 'WhatsApp Destek',
      };
    }

    if (account.restrictionReason === 'disabled') {
      return {
        title: 'Hesabınız geçici olarak kısıtlandı',
        description: 'Kredi, süre ve işlem geçmişi dışındaki özellikler kapatıldı. Hesabınızı yeniden etkinleştirmek için bizimle iletişime geçin.',
        action: 'support',
        actionLabel: 'WhatsApp Destek',
      };
    }

    return {
      title: 'Üyelik süreniz doldu',
      description: expiredResellerAccess
        ? 'Süreniz dolduğu için kendi yayın özellikleriniz kapatıldı. Alt kullanıcılarınızı yönetmeye devam edebilirsiniz; tüm özellikleri açmak için hesabınıza süre ekleyin.'
        : 'Süreniz dolduğu için bazı özellikler kısıtlandı. Tüm özellikleri yeniden açmak için hesabınıza süre ekleyin.',
      action: 'duration',
      actionLabel: 'Süre Ekle / Uzat',
    };
  }, [account.restrictionReason, expiredResellerAccess]);

  useEffect(() => {
    if (!account.isRestricted) return;

    if (expiredResellerAccess) {
      // Yalnızca kendi yayın sayfalarından ve içerik pencerelerinden çıkarılır; alt kullanıcı işlemleri sürer.
      setActiveItem((current) => (canRestrictedAccountOpenRoute(current, true) ? current : 'overview'));
      setAdminMenuOpen(false);
      setWindowsDownloadModalOpen(false);
      setMovieDetailDialog({ open: false, movie: null, contentType: 'movie' });
      setCommentsDialog({ open: false, content: null, contentType: null });
      setReviewModalOpen(false);
      return;
    }

    setActiveItem('overview');
    setAdminMenuOpen(false);
    setAccountModalOpen(false);
    setCategoryModalOpen(false);
    setAdultModalOpen(false);
    setSecurityModalOpen(false);
    setWindowsDownloadModalOpen(false);
    setMovieDetailDialog({ open: false, movie: null, contentType: 'movie' });
    setCommentsDialog({ open: false, content: null, contentType: null });
    setReviewModalOpen(false);
    setManageModalOpen(false);
    setSelectedSubUser(null);
    setModalTargetUser(null);
    setSetupTargetUser(null);
    setSetupDevice(null);
    setSetupSourceAppId(null);
  }, [account.isRestricted, expiredResellerAccess]);

  const visibleNavigationItems = useMemo(
    () => navigationItems.filter((item) => item.id !== 'user-actions' || isReseller)
      .map((item) => item.id === 'bonus' && creditCurrency !== 'TRY' ? { ...item, label: 'Referanslar' } : item),
    [creditCurrency, isReseller]
  );
  const resolvedSupportUnreadCount = useMemo(() => {
    if (supportUnreadCount !== null && supportUnreadCount !== undefined) {
      return Math.max(0, Math.floor(Number(supportUnreadCount) || 0));
    }
    if (!Array.isArray(supportTickets)) return 0;
    return supportTickets.reduce(
      (total, ticket) => total + Math.max(0, Number(ticket?.unread_replies) || 0),
      0
    );
  }, [supportTickets, supportUnreadCount]);

  const resolvedOverviewCards = useMemo(() => {
    const values = {
      Kredi: formatCreditAmount(account.credit, creditCurrency, eurTryRate),
      'Bitiş Tarihi': account.expiryLabel,
      'Alt Kullanıcılar': account.subUserCount == null ? '—' : account.subUserCount.toLocaleString('tr-TR'),
      'Davet Edilenler': account.invitedCount == null ? '—' : account.invitedCount.toLocaleString('tr-TR'),
    };
    return overviewCards.map((card) => ({
      ...card,
      value: values[card.label],
      meta: card.label === 'Bitiş Tarihi' ? account.expiryTimeLabel : '',
      disabled: (card.resellerOnly && !isReseller)
        || (account.isRestricted && !canRestrictedAccountNavigate(card.action, expiredResellerAccess)),
    }));
  }, [account, creditCurrency, eurTryRate, expiredResellerAccess, isReseller]);

  const copyAccountValue = async (value, field) => {
    if (!value) return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = value;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();

        try {
          document.execCommand('copy');
        } finally {
          textarea.remove();
        }
      }

      setCopiedAccountField(field);
      window.setTimeout(() => {
        setCopiedAccountField((current) => (current === field ? null : current));
      }, 1400);
    } catch (error) {
      setCopiedAccountField(null);
    }
  };

  const selectNavigation = (id, options) => {
    // Kurulum menüde kapalıdır; süresi dolan bayi oraya yalnızca bir alt kullanıcı için (openSetup) geçer.
    const expiredResellerSetup = expiredResellerAccess && id === 'setup' && Boolean(options?.state?.setupTargetUser);
    if (account.isRestricted && !expiredResellerSetup && !canRestrictedAccountNavigate(id, expiredResellerAccess)) {
      setActiveItem('overview');
      setSidebarOpen(false);
      setAdminMenuOpen(false);
      return;
    }

    setActiveItem(id);
    setSidebarOpen(false);
    setAdminMenuOpen(false);
    onNavigateSection?.(id, options);
  };

  const openSeriesCategory = (platformId) => {
    const normalizedPlatformId = String(platformId || '').trim();
    selectNavigation(
      'series',
      normalizedPlatformId ? { state: { seriesPlatform: normalizedPlatformId } } : undefined
    );
  };

  const openDurationModal = (targetUser = null) => {
    if (targetUser && isBlockedFor(targetUser)) return;
    setSidebarOpen(false);
    setProfileOpen(false);
    setCreditModalOpen(false);
    setModalTargetUser(targetUser);
    setDurationModalOpen(true);
  };

  const openCreditModal = async () => {
    setSidebarOpen(false);
    setProfileOpen(false);
    setDurationModalOpen(false);
    setModalTargetUser(null);
    setCreditModalOpen(false);

    if (creditOpenInFlightRef.current) return;
    creditOpenInFlightRef.current = true;

    try {
      const identity = getLegacyCreditIdentity(userInfo);
      let nextFlow;

      if (creditProvider || creditPreview) {
        const forcedSetting = creditProvider || 'stripe';
        nextFlow = {
          ...resolveLegacyCreditModalSetting(forcedSetting),
          ...identity,
          setting: forcedSetting,
          userModal: forcedSetting,
          settings: creditSettingsCache,
          isTR: true,
          countryCode: 'TR',
        };
      } else {
        nextFlow = await resolveLegacyCreditFlow({
          userInfo,
          cachedSettings: creditSettingsCache,
        });
      }

      if (nextFlow.settings) setCreditSettingsCache(nextFlow.settings);
      if (nextFlow.type === 'kapali') {
        setCreditFlow(null);
        return;
      }

      setCreditFlow({ ...nextFlow, currency: creditCurrency });
      setCreditModalOpen(true);
    } finally {
      creditOpenInFlightRef.current = false;
    }
  };

  const openCreditWhatsapp = (userId) => {
    if (typeof window === 'undefined') return;
    window.open(
      buildLegacyCreditWhatsappUrl(userId || creditFlow?.userId, creditFlow?.currency || creditCurrency),
      '_blank',
      'noopener,noreferrer',
    );
  };

  const openCategoryModal = (targetUser = null) => {
    if (isBlockedFor(targetUser)) return;
    setProfileOpen(false);
    setModalTargetUser(targetUser);
    setCategoryModalOpen(true);
  };

  const openAccountModal = (initialTab = 'personal', targetUser = null) => {
    if (isBlockedFor(targetUser)) return;
    setProfileOpen(false);
    setModalTargetUser(targetUser);
    setAccountModalInitialTab(initialTab);
    setAccountModalOpen(true);
  };

  const openSecurityModal = (targetUser = null) => {
    if (isBlockedFor(targetUser)) return;
    setProfileOpen(false);
    setModalTargetUser(targetUser);
    setSecurityModalOpen(true);
  };

  const openAdultModal = (targetUser = null) => {
    if (isBlockedFor(targetUser)) return;
    setProfileOpen(false);
    setModalTargetUser(targetUser);
    setAdultModalOpen(true);
  };

  const openSubUserManagement = (subUser) => {
    if (isBlockedFor(subUser)) return;
    setProfileOpen(false);
    setSelectedSubUser(subUser);
    setManageModalOpen(true);
  };

  const openSetup = (targetUser = null) => {
    if (isBlockedFor(targetUser)) return;
    setManageModalOpen(false);
    setSetupTargetUser(targetUser);
    setSetupDevice(null);
    setSetupSourceAppId(null);
    selectNavigation(
      'setup',
      targetUser ? { state: { setupTargetUser: targetUser } } : undefined
    );
  };

  const getSetupNavigationOptions = (options = {}) => {
    if (!setupTargetUser) return options;

    return {
      ...options,
      state: {
        ...(options.state || {}),
        setupTargetUser,
      },
    };
  };

  const openSetupDetail = (device, options = {}) => {
    if (isBlockedFor(setupTargetUser)) return;
    const normalizedDevice = String(device || '').trim();
    if (!normalizedDevice) return;

    setSidebarOpen(false);
    setActiveItem('setup');
    setSetupDevice(normalizedDevice);
    setSetupSourceAppId(options.state?.sourceAppId || null);
    onNavigateSetupDetail?.(normalizedDevice, getSetupNavigationOptions(options));
  };

  const handleNotificationNavigation = (notification) => {
    if (account.isRestricted) return;
    if (!notification?.link_data) return;
    try {
      const linkData = typeof notification.link_data === 'string'
        ? JSON.parse(notification.link_data)
        : notification.link_data;
      if (linkData?.sayfa_tipi === 'kurulum-detay' && linkData.sayfa_ref) {
        openSetupDetail(linkData.sayfa_ref);
      }
    } catch (error) {
      // Eski temadaki gibi geçersiz link_data bildirimi yönlendirme yapmaz.
    }
  };

  const closeSetupDetail = () => {
    setSetupDevice(null);
    setSetupSourceAppId(null);
    selectNavigation('setup', getSetupNavigationOptions());
  };

  const openWindowsDownloadModal = (content = null, contentType = null) => {
    if (account.isRestricted) return;
    setSidebarOpen(false);
    setProfileOpen(false);
    setSelectedWindowsContent(content);
    setWindowsDownloadContentType(content ? (contentType || 'channel') : null);
    setWindowsDownloadModalOpen(true);
  };

  const openMovieDetailModal = async (movie) => {
    if (account.isRestricted) return;
    setSidebarOpen(false);
    setProfileOpen(false);
    setWindowsDownloadModalOpen(false);
    setReviewModalOpen(false);
    setCommentsDialog({ open: false, content: null, contentType: null });
    setMovieDetailDialog({ open: true, movie, contentType: 'movie' });

    if (typeof onLoadMovieDetail === 'function') {
      try {
        const detail = await onLoadMovieDetail(movie?.raw || movie);
        setMovieDetailDialog((current) => (
          current.open && current.contentType === 'movie'
            ? { ...current, movie: detail }
            : current
        ));
      } catch (error) {
        // Detay isteği başarısız olursa liste verisiyle pencere açık kalır.
      }
    }
  };

  const openSeriesDetailModal = async (series) => {
    if (account.isRestricted) return;
    setSidebarOpen(false);
    setProfileOpen(false);
    setWindowsDownloadModalOpen(false);
    setReviewModalOpen(false);
    setCommentsDialog({ open: false, content: null, contentType: null });
    setMovieDetailDialog({ open: true, movie: series, contentType: 'series' });

    if (typeof onLoadSeriesDetail === 'function') {
      try {
        const detail = await onLoadSeriesDetail(series?.raw || series);
        setMovieDetailDialog((current) => (
          current.open && current.contentType === 'series'
            ? { ...current, movie: detail }
            : current
        ));
      } catch (error) {
        // Detay isteği başarısız olursa liste verisiyle pencere açık kalır.
      }
    }
  };

  const openSelectedContentInWindows = () => {
    const selectedContent = movieDetailDialog.movie;
    const selectedContentType = movieDetailDialog.contentType || 'movie';
    setMovieDetailDialog({ open: false, movie: null, contentType: 'movie' });
    openWindowsDownloadModal(selectedContent, selectedContentType);
  };

  const openCommentsModal = async (content, contentType) => {
    if (account.isRestricted) return;
    setSidebarOpen(false);
    setProfileOpen(false);
    setWindowsDownloadModalOpen(false);
    setReviewModalOpen(false);
    setCommentsDialog({ open: true, content: { ...content, comments: [], commentsLoading: true }, contentType });

    if (typeof onLoadComments === 'function') {
      const pageType = contentType === 'setup' ? 'kurulum-detay' : contentType === 'series' ? 'dizi' : 'film';
      const pageRef = contentType === 'setup'
        ? content?.id || ''
        : contentType === 'series'
          ? content?.series_id || content?.id || ''
          : content?.stream_id || content?.id || '';
      try {
        const comments = await onLoadComments({ pageType, pageRef: String(pageRef) });
        setCommentsDialog((current) => (
          current.open
            ? { ...current, content: { ...current.content, comments, commentsLoading: false } }
            : current
        ));
      } catch (error) {
        setCommentsDialog((current) => (
          current.open
            ? { ...current, content: { ...current.content, comments: [], commentsLoading: false, commentsError: error?.message || 'Yorumlar alınamadı.' } }
            : current
        ));
      }
    } else {
      setCommentsDialog((current) => (
        current.open
          ? { ...current, content: { ...current.content, comments: [], commentsLoading: false, commentsError: 'Yorum bağlantısı kullanılamıyor.' } }
          : current
      ));
    }
  };

  const handleToggleCommentHelpful = async (commentId) => {
    if (account.isRestricted) throw new Error(RESTRICTED_ACTION_MESSAGE);
    if (typeof onToggleCommentHelpful !== 'function') throw new Error('Faydalı yorum bağlantısı kullanılamıyor.');
    if (typeof onLoadComments !== 'function') throw new Error('Yorum yenileme bağlantısı kullanılamıyor.');
    await onToggleCommentHelpful(commentId);

    const currentContent = commentsDialog.content;
    const currentType = commentsDialog.contentType;
    const pageType = currentType === 'setup' ? 'kurulum-detay' : currentType === 'series' ? 'dizi' : 'film';
    const pageRef = currentType === 'setup'
      ? currentContent?.id || ''
      : currentType === 'series'
        ? currentContent?.series_id || currentContent?.id || ''
        : currentContent?.stream_id || currentContent?.id || '';
    const comments = await onLoadComments({ pageType, pageRef: String(pageRef) });
    setCommentsDialog((current) => (
      current.open
        ? { ...current, content: { ...current.content, comments, commentsLoading: false, commentsError: '' } }
        : current
    ));
  };

  const openStandaloneReviewModal = (content, contentType) => {
    if (account.isRestricted) return;
    setSidebarOpen(false);
    setProfileOpen(false);
    setWindowsDownloadModalOpen(false);
    setCommentsDialog({ open: false, content, contentType });
    setReviewModalOpen(true);
  };

  const openReferences = () => {
    if (account.isRestricted) return;
    setProfileOpen(false);
    selectNavigation('bonus');
  };

  const openTransactionHistory = () => {
    setProfileOpen(false);
    setTransactionHistoryModalOpen(true);
  };

  const handleNavigationItemClick = (id) => {
    if (account.isRestricted && !canRestrictedAccountNavigate(id, expiredResellerAccess)) return;

    if (id === 'credit') {
      openCreditModal();
      return;
    }

    if (id === 'extend') {
      openDurationModal();
      return;
    }

    if (id === 'setup') {
      setSetupTargetUser(null);
      setSetupDevice(null);
    }

    selectNavigation(id);
  };

  const handleSearchNavigation = (page, result) => {
    if (account.isRestricted) return;
    const navigationId = page === 'filmler' ? 'movies' : 'series';
    selectNavigation(navigationId);
    onNavigateToPage?.(page, result);
  };

  const modalUserInfo = modalTargetUser
    ? {
        ...userInfo,
        _targetUserId: modalTargetUser.id,
        user: {
          ...(userInfo?.user || {}),
          ...modalTargetUser,
        },
        user_info: {
          ...(userInfo?.user_info || {}),
          ...modalTargetUser,
        },
      }
    : userInfo;
  const setupUserInfo = setupTargetUser
    ? {
        ...userInfo,
        user_info: {
          ...(userInfo?.user_info || {}),
          ...setupTargetUser,
        },
      }
    : userInfo;
  const isAdminUser = Number(userInfo?.user_info?.is_admin) === 1;
  const hasAdminAccess = isAdminUser || adminPreview;
  const isAdminSectionActive = ADMIN_SECTION_IDS.includes(activeItem);
  const canRenderAdminSection = hasAdminAccess && isAdminSectionActive;

  return (
    <div className={`tvup-shell${sidebarOpen ? ' is-sidebar-open' : ''}`}>
      <div key={routeProgressKey} className="tvup-route-progress" aria-hidden="true">
        <span />
      </div>

      <button
        className="tvup-sidebar-backdrop"
        type="button"
        aria-label="Menüyü kapat"
        onClick={() => setSidebarOpen(false)}
      />

      <aside className="tvup-sidebar" aria-label="Kullanıcı paneli menüsü">
        <div className="tvup-sidebar-brand">
          <img src="/assets/images/tivigo-wordmark.png" alt="Tivigo" />
          <button
            className="tvup-sidebar-close"
            type="button"
            aria-label="Menüyü kapat"
            onClick={() => setSidebarOpen(false)}
          >
            <span />
            <span />
          </button>
        </div>

        <div className="tvup-sidebar-body">
          <p className="tvup-sidebar-label">KULLANICI MENÜSÜ</p>
          <nav className="tvup-navigation">
            {visibleNavigationItems.map((item) => {
              const isCreditAction = item.id === 'credit';
              const isDurationAction = item.id === 'extend';
              const isModalAction = isCreditAction || isDurationAction;
              const isActive = isCreditAction
                ? creditModalOpen
                : isDurationAction
                  ? durationModalOpen
                  : activeItem === item.id;
              const controlledModalId = isCreditAction
                ? 'tvup-credit-modal'
                : isDurationAction
                  ? 'tvup-duration-modal'
                  : undefined;
              const navigationDisabled = account.isRestricted && !canRestrictedAccountNavigate(item.id, expiredResellerAccess);
              const badgeCount = item.id === 'support' && !navigationDisabled
                ? resolvedSupportUnreadCount
                : 0;

              return (
                <button
                  key={item.id}
                  type="button"
                  className={`tvup-nav-item${isActive ? ' is-active' : ''}`}
                  aria-current={!isModalAction && isActive ? 'page' : undefined}
                  aria-haspopup={isModalAction ? 'dialog' : undefined}
                  aria-expanded={isModalAction ? isActive : undefined}
                  aria-controls={controlledModalId}
                  aria-disabled={navigationDisabled || undefined}
                  disabled={navigationDisabled}
                  title={navigationDisabled ? RESTRICTED_ACTION_MESSAGE : undefined}
                  onClick={navigationDisabled ? undefined : () => handleNavigationItemClick(item.id)}
                >
                  <MaskIcon name={item.icon} />
                  <span>{item.label}</span>
                  {badgeCount > 0 && (
                    <strong
                      className="tvup-nav-badge"
                      aria-label={`${badgeCount} okunmamış destek yanıtı`}
                      title={`${badgeCount} yeni destek yanıtı`}
                    >
                      {badgeCount > 99 ? '99+' : badgeCount}
                    </strong>
                  )}
                </button>
              );
            })}
          </nav>

        </div>

      </aside>

      <div className="tvup-workspace">
        <header className={`tvup-header${headerScrolled ? ' is-scrolled' : ''}`}>
          <div className="tvup-header-inner">
            <button
              className="tvup-mobile-menu"
              type="button"
              aria-label="Menüyü aç"
              aria-expanded={sidebarOpen}
              onClick={() => setSidebarOpen(true)}
            >
              <span />
              <span />
              <span />
            </button>

            {hasAdminAccess && !account.isRestricted && (
              <div
                className="tvup-admin-menu-wrap"
                ref={adminMenuRef}
                onBlur={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget)) setAdminMenuOpen(false);
                }}
              >
                <button
                  className={`tvup-admin-menu-trigger${isAdminSectionActive ? ' is-active' : ''}`}
                  ref={adminMenuTriggerRef}
                  type="button"
                  aria-label="Yönetim menüsünü aç"
                  aria-controls="tvup-admin-mega-menu"
                  aria-expanded={adminMenuOpen}
                  onClick={() => {
                    setProfileOpen(false);
                    setAdminMenuOpen((open) => !open);
                  }}
                >
                  <AdminPanelSettingsRoundedIcon aria-hidden="true" />
                  <span>Yönetim</span>
                  <KeyboardArrowDownRoundedIcon
                    className={`tvup-admin-menu-chevron${adminMenuOpen ? ' is-open' : ''}`}
                    aria-hidden="true"
                  />
                </button>

                {adminMenuOpen && (
                  <div
                    id="tvup-admin-mega-menu"
                    className="tvup-admin-mega-menu"
                    role="navigation"
                    aria-label="Yönetim sayfaları"
                  >
                    <div className="tvup-admin-mega-head">
                      <span className="tvup-admin-mega-emblem" aria-hidden="true">
                        <AdminPanelSettingsRoundedIcon />
                      </span>
                      <span>
                        <small>YETKİLİ ERİŞİM</small>
                        <b>Yönetim Merkezi</b>
                      </span>
                      <em>{ADMIN_SECTIONS.length} bölüm</em>
                    </div>

                    <div className="tvup-admin-mega-grid">
                      {ADMIN_SECTIONS.map((section) => {
                        const SectionIcon = section.icon;
                        const isActive = activeItem === section.id;

                        return (
                          <button
                            key={section.id}
                            className={`tvup-admin-mega-card tone-${section.tone}${isActive ? ' is-active' : ''}`}
                            type="button"
                            aria-current={isActive ? 'page' : undefined}
                            onClick={() => selectNavigation(section.id)}
                          >
                            <span className="tvup-admin-mega-card-icon"><SectionIcon /></span>
                            <span className="tvup-admin-mega-card-copy">
                              <b>{section.title}</b>
                              <small>{section.description}</small>
                            </span>
                            <ArrowForwardRoundedIcon className="tvup-admin-mega-card-arrow" />
                          </button>
                        );
                      })}
                    </div>

                    <div className="tvup-admin-mega-footer">
                      <span><i /> {isAdminUser ? 'Yönetici oturumu' : 'Tema önizlemesi'}</span>
                      <small>{isAdminUser ? 'Bu alan yalnız yetkili hesaplarda görünür.' : 'Admin sayfaları tasarım kontrolü için açık.'}</small>
                    </div>
                  </div>
                )}
              </div>
            )}

            <UserPanelSearch
              userInfo={userInfo}
              previewMode={searchPreview}
              disabled={account.isRestricted}
              onNavigateToPage={handleSearchNavigation}
            />

            <div className="tvup-header-actions">
              {!account.isRestricted && (
                <>
                  <button
                    className="tvup-icon-button"
                    type="button"
                    aria-label="Canlı Destek"
                    data-tooltip="Canlı Destek"
                    onClick={openSupheroChat}
                  >
                    <MaskIcon name="headphones" />
                  </button>
                  <UserPanelNotifications currency={creditCurrency} onNavigate={handleNotificationNavigation} />
                </>
              )}
              <a
                className="tvup-icon-button tone-telegram"
                href={TELEGRAM_SUPPORT_URL}
                target="_blank"
                rel="noreferrer"
                aria-label="Telegram Destek Hattı"
                data-tooltip="Telegram Destek Hattı"
              >
                <TelegramIcon className="tvup-action-svg" />
              </a>
              <a
                className="tvup-icon-button tone-whatsapp"
                href={WHATSAPP_SUPPORT_URL}
                target="_blank"
                rel="noreferrer"
                aria-label="WhatsApp Destek Hattı"
                data-tooltip="WhatsApp Destek Hattı"
              >
                <WhatsAppIcon className="tvup-action-svg" />
              </a>
              <div className="tvup-profile-wrap" ref={profileMenuRef}>
                <button
                  className="tvup-profile-button"
                  type="button"
                  aria-label="Hesap menüsü"
                  aria-expanded={profileOpen}
                  onClick={() => setProfileOpen((open) => !open)}
                >
                  <span>{user.initial}</span>
                  <b>{user.username}</b>
                  <KeyboardArrowDownRoundedIcon
                    className={`tvup-profile-chevron${profileOpen ? ' is-open' : ''}`}
                    aria-hidden="true"
                  />
                </button>
                {profileOpen && (
                  <div className="tvup-profile-menu" role="menu" aria-label="Hesap hızlı erişim menüsü">
                    <div className="tvup-profile-menu-head">
                      <span className="tvup-profile-menu-avatar" aria-hidden="true">{user.initial}</span>
                      <span className="tvup-profile-menu-user">
                        <small>OTURUM AÇIK</small>
                        <b>{user.displayName}</b>
                      </span>
                      <i className="tvup-profile-menu-online" aria-label="Çevrimiçi" />
                    </div>
                    <div className="tvup-profile-menu-section">
                      <span className="tvup-profile-menu-label">HIZLI ERİŞİM</span>
                    <div className="tvup-profile-menu-actions">
                      <button
                        className="tvup-profile-menu-action tone-account"
                        type="button"
                        role="menuitem"
                        aria-haspopup="dialog"
                        aria-controls="tvup-account-modal"
                        disabled={account.isRestricted}
                        title={account.isRestricted ? RESTRICTED_ACTION_MESSAGE : undefined}
                        onClick={account.isRestricted ? undefined : () => openAccountModal('personal')}
                      >
                        <span className="tvup-profile-menu-action-icon"><PersonOutlineRoundedIcon /></span>
                        <span className="tvup-profile-menu-action-copy"><b>Hesabı Düzenle</b><small>Kişisel ve iletişim bilgilerin</small></span>
                        <ArrowForwardRoundedIcon className="tvup-profile-menu-action-arrow" />
                      </button>
                      <button
                        className="tvup-profile-menu-action tone-security"
                        type="button"
                        role="menuitem"
                        aria-haspopup="dialog"
                        aria-controls="tvup-security-modal"
                        disabled={account.isRestricted}
                        title={account.isRestricted ? RESTRICTED_ACTION_MESSAGE : undefined}
                        onClick={account.isRestricted ? undefined : () => openSecurityModal()}
                      >
                        <span className="tvup-profile-menu-action-icon"><ShieldOutlinedIcon /></span>
                        <span className="tvup-profile-menu-action-copy"><b>Güvenlik</b><small>Giriş ve güvenlik kayıtların</small></span>
                        <ArrowForwardRoundedIcon className="tvup-profile-menu-action-arrow" />
                      </button>
                      <button
                        className="tvup-profile-menu-action tone-references"
                        type="button"
                        role="menuitem"
                        disabled={account.isRestricted}
                        title={account.isRestricted ? RESTRICTED_ACTION_MESSAGE : undefined}
                        onClick={account.isRestricted ? undefined : openReferences}
                      >
                        <span className="tvup-profile-menu-action-icon"><PersonAddRoundedIcon /></span>
                        <span className="tvup-profile-menu-action-copy"><b>Referanslar</b><small>{creditCurrency === 'TRY' ? 'Davet et, 250 TL kazan' : 'Davet ettiğiniz kullanıcılar'}</small></span>
                        <ArrowForwardRoundedIcon className="tvup-profile-menu-action-arrow" />
                      </button>
                      <button
                        className="tvup-profile-menu-action tone-history"
                        type="button"
                        role="menuitem"
                        aria-haspopup="dialog"
                        aria-controls="tvup-transaction-history-modal"
                        onClick={openTransactionHistory}
                      >
                        <span className="tvup-profile-menu-action-icon"><HistoryRoundedIcon /></span>
                        <span className="tvup-profile-menu-action-copy"><b>İşlem Geçmişi</b><small>Kredi hareketleriniz</small></span>
                        <ArrowForwardRoundedIcon className="tvup-profile-menu-action-arrow" />
                      </button>
                    </div>
                    </div>
                    <div className="tvup-profile-menu-footer">
                      <span><b>Güvenli oturum</b><small>İşiniz bittiğinde çıkış yapın.</small></span>
                      <button className="tvup-profile-logout" type="button" role="menuitem" onClick={onLogout}><LogoutRoundedIcon /><span>Çıkış yap</span></button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        <div
          className="tvup-scroll-area"
          ref={scrollAreaRef}
          role="region"
          aria-label="Dashboard içeriği"
          tabIndex={0}
        >
          <main className={`tvup-main${activeItem === 'overview' ? ' is-overview' : ''}`}>
          {canRenderAdminSection ? (
            <React.Suspense
              fallback={(
                <UserPanelTableLoading
                  rows={5}
                  columns={6}
                  label="Yönetim sayfası hazırlanıyor"
                  className="is-admin"
                />
              )}
            >
              <UserPanelAdmin
                key={activeItem}
                section={activeItem}
                onNavigate={selectNavigation}
                initialFilter={initialAdminFilter}
              />
            </React.Suspense>
          ) : activeItem === 'live' ? (
            <UserPanelWebTV
              channels={Array.isArray(liveTv?.channels) ? liveTv.channels : []}
              categories={Array.isArray(liveTv?.categories) ? liveTv.categories : []}
              loading={liveTvLoading || liveTv == null}
              onOpenWindowsModal={openWindowsDownloadModal}
            />
          ) : activeItem === 'movies' ? (
            <UserPanelMovies
              movies={Array.isArray(movies) ? movies : []}
              loading={moviesLoading || movies == null}
              onMovieSelect={openMovieDetailModal}
              onMovieComments={(movie) => openCommentsModal(movie, 'movie')}
            />
          ) : activeItem === 'series' ? (
            <UserPanelSeries
              series={Array.isArray(series) ? series : []}
              loading={seriesLoading || series == null}
              initialPlatform={initialSeriesPlatform}
              onSeriesSelect={openSeriesDetailModal}
              onSeriesComments={(series) => openCommentsModal(series, 'series')}
            />
          ) : activeItem === 'user-actions' ? (
            <UserPanelSubUsers
              data={subUsersData}
              loading={subUsersLoading}
              onCreateUser={onCreateSubUser}
              onManageUser={openSubUserManagement}
              resellerInfo={resellerInfo}
              onLoadTestLimit={onLoadResellerTestLimit}
            />
          ) : activeItem === 'setup' ? (
            setupDevice ? (
              <UserPanelSetupDetail
                userInfo={setupUserInfo}
                targetUsername={setupTargetUser?.username || null}
                device={setupDevice}
                sourceAppId={setupSourceAppId}
                onBack={closeSetupDetail}
                onOpenM3u={(sourceAppId = null) => openSetupDetail(
                  'm3u',
                  sourceAppId ? { state: { sourceAppId } } : undefined
                )}
                onOpenReview={(application) => openStandaloneReviewModal(application, 'setup')}
                onLoadComments={onLoadComments}
                onToggleCommentHelpful={onToggleCommentHelpful}
              />
            ) : (
              <UserPanelSetup
                targetUsername={setupTargetUser?.username || null}
                onOpenSetupDetail={openSetupDetail}
              />
            )
          ) : activeItem === 'support' ? (
            <UserPanelSupport
              tickets={Array.isArray(supportTickets) ? supportTickets : []}
              loading={supportLoading}
              onCreateTicket={onCreateTicket}
              onReplyTicket={onReplyTicket}
              onEditReply={onEditTicketReply}
              onToggleTicket={onSetTicketStatus}
              onLoadTicket={onLoadTicket}
            />
          ) : activeItem === 'bonus' ? (
            <UserPanelReferrals
              referrals={Array.isArray(referrals) ? referrals : []}
              referralCode={referralCode}
              loading={referralsLoading}
              onClaimBonus={onClaimReferralBonus}
              currency={creditCurrency}
              eurTryRate={eurTryRate}
            />
          ) : (
          <>
          {account.isRestricted && (
            <section
              className={`tvup-account-restriction-alert is-${account.restrictionReason || 'expired'}`}
              role="alert"
              aria-live="polite"
            >
              <span className="tvup-account-restriction-icon" aria-hidden="true">
                <WarningAmberRoundedIcon />
              </span>
              <span className="tvup-account-restriction-copy">
                <strong>{restrictionNotice.title}</strong>
                <span>{restrictionNotice.description}</span>
              </span>
              {restrictionNotice.action === 'duration' ? (
                <button type="button" onClick={() => openDurationModal()}>
                  <MoreTimeRoundedIcon />
                  <span>{restrictionNotice.actionLabel}</span>
                  <ArrowForwardRoundedIcon />
                </button>
              ) : (
                <a href={WHATSAPP_SUPPORT_URL} target="_blank" rel="noreferrer">
                  <WhatsAppIcon />
                  <span>{restrictionNotice.actionLabel}</span>
                  <ArrowForwardRoundedIcon />
                </a>
              )}
            </section>
          )}
          <section className={`tvup-hero-panel is-${dashboardHeroPlatform.id}${dashboardHeroPlatform.dashboardBanner ? ' is-platform-banner-only' : ''}${account.isRestricted ? ' is-restricted' : ''}`}>
            {dashboardHeroPlatform.dashboardBanner ? (
              <button
                type="button"
                className="tvup-dashboard-platform-banner"
                disabled={account.isRestricted}
                title={account.isRestricted ? RESTRICTED_ACTION_MESSAGE : undefined}
                onClick={account.isRestricted ? undefined : () => openWindowsDownloadModal()}
                aria-label={`${dashboardHeroPlatform.name} uygulamasını aç`}
              >
                <img
                  src={dashboardHeroPlatform.dashboardBanner}
                  width={dashboardHeroPlatform.dashboardBannerSize?.[0]}
                  height={dashboardHeroPlatform.dashboardBannerSize?.[1]}
                  alt={dashboardHeroPlatform.dashboardBannerLabel}
                />
              </button>
            ) : (
            <>
            <div className="tvup-hero-copy">
              <p className="tvup-panel-kicker"><span /> TIVIGO FOR {dashboardHeroPlatform.name.toUpperCase()}</p>
              <h2>
                {dashboardHeroPlatform.headlinePrefix && <>{dashboardHeroPlatform.headlinePrefix} </>}
                <span>{dashboardHeroPlatform.headlineAccent}</span>{' '}
                {dashboardHeroPlatform.headlineSuffix}
              </h2>
              <p>
                {dashboardHeroPlatform.description}
              </p>
              <a
                className={`tvup-primary-button${account.isRestricted ? ' is-disabled' : ''}`}
                href={`#${dashboardHeroPlatform.id}-download`}
                aria-haspopup="dialog"
                aria-controls="tvup-windows-download-modal"
                aria-disabled={account.isRestricted || undefined}
                tabIndex={account.isRestricted ? -1 : 0}
                title={account.isRestricted ? RESTRICTED_ACTION_MESSAGE : undefined}
                onClick={(event) => {
                  event.preventDefault();
                  if (account.isRestricted) return;
                  openWindowsDownloadModal();
                }}
              >
                <DashboardHeroPlatformIcon className="tvup-windows-button-icon" />
                {dashboardHeroPlatform.cta} <span aria-hidden="true">↓</span>
              </a>
              <div className="tvup-download-meta" aria-label={`${dashboardHeroPlatform.name} uygulaması bilgileri`}>
                <span><i aria-hidden="true" /> {dashboardHeroPlatform.metaPrimary}</span>
                <span>{dashboardHeroPlatform.metaSecondary}</span>
              </div>
            </div>

            <div className="tvup-hero-visual">
              <div className={`tvup-windows-preview${dashboardHeroPlatform.mobile ? ' is-mobile-app' : ''}`}>
                <div className="tvup-windows-preview-bar">
                  <span className="tvup-window-controls" aria-hidden="true"><i /><i /><i /></span>
                  <span><DashboardHeroPlatformIcon /> Tivigo for {dashboardHeroPlatform.name}</span>
                  <small>{dashboardHeroPlatform.previewVersion}</small>
                </div>
                <div className="tvup-windows-preview-media">
                  <video
                    src={dashboardHeroPlatform.main}
                    poster={dashboardHeroPlatform.poster}
                    width={dashboardHeroPlatform.mainSize?.[0]}
                    height={dashboardHeroPlatform.mainSize?.[1]}
                    aria-label={`Tivigo ${dashboardHeroPlatform.name} uygulaması tanıtım turu`}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                  />
                  <span><i aria-hidden="true" /> {dashboardHeroPlatform.mainLabel}</span>
                </div>
              </div>
            </div>
            </>
            )}
          </section>

          <section className="tvup-overview-section">
            <div className="tvup-section-heading">
              <div>
                <h2>{timeBasedGreeting}</h2>
              </div>
            </div>

            <div className="tvup-overview-grid">
              {resolvedOverviewCards.map((card, index) => {
                const controlledModalId = card.action === 'credit'
                  ? 'tvup-credit-modal'
                  : card.action === 'extend'
                    ? 'tvup-duration-modal'
                    : undefined;
                const disabledTitle = card.resellerOnly && !isReseller
                  ? 'Alt kullanıcılar yalnızca bayi hesaplarında kullanılabilir.'
                  : card.disabled
                    ? RESTRICTED_ACTION_MESSAGE
                    : undefined;

                return (
                <button
                  key={card.label}
                  type="button"
                  className={`tvup-overview-card tone-${card.tone}`}
                  aria-haspopup={controlledModalId ? 'dialog' : undefined}
                  aria-controls={controlledModalId}
                  disabled={card.disabled}
                  title={disabledTitle}
                  onClick={() => handleNavigationItemClick(card.action)}
                >
                  <div className="tvup-overview-top">
                    <span className="tvup-card-index">{String(index + 1).padStart(2, '0')}</span>
                    <MaskIcon name={card.icon} />
                  </div>
                  <strong className={card.meta ? 'tvup-overview-expiry-value' : undefined}>
                    {card.value}
                    {card.meta ? <small className="tvup-overview-expiry-time">{card.meta}</small> : null}
                  </strong>
                  <b>{card.label}</b>
                </button>
                );
              })}
            </div>
          </section>

          <section className="tvup-lower-grid">
            <article className="tvup-surface-card tvup-quick-card">
              <div className="tvup-card-heading">
                <div>
                  <h2>Hızlı işlemler</h2>
                </div>
              </div>
              <div className="tvup-quick-grid">
                {quickActions.map((action) => {
                  const ActionIcon = action.icon;
                  const controlledModalId = action.action === 'credit'
                    ? 'tvup-credit-modal'
                    : action.action === 'extend'
                      ? 'tvup-duration-modal'
                      : action.action === 'categories'
                        ? 'tvup-category-modal'
                        : action.action === 'windows'
                          ? 'tvup-windows-download-modal'
                        : undefined;
                  const actionHandler = action.action === 'credit'
                    ? openCreditModal
                    : action.action === 'extend'
                      ? () => openDurationModal()
                      : action.action === 'categories'
                        ? () => openCategoryModal()
                        : action.action === 'setup'
                          ? () => openSetupDetail(action.setupDetailId)
                        : action.action === 'windows'
                          ? () => openWindowsDownloadModal()
                        : undefined;
                  const actionAllowedWhileRestricted = action.action === 'credit' || action.action === 'extend';
                  const actionDisabled = account.isRestricted && !actionAllowedWhileRestricted;

                  return (
                    <button
                      key={action.label}
                      type="button"
                      className={`tvup-quick-item is-${action.variant} tone-${action.tone}`}
                      aria-haspopup={controlledModalId ? 'dialog' : undefined}
                      aria-controls={controlledModalId}
                      aria-disabled={actionDisabled || undefined}
                      disabled={actionDisabled}
                      title={actionDisabled ? RESTRICTED_ACTION_MESSAGE : undefined}
                      onClick={actionDisabled ? undefined : actionHandler}
                    >
                      <span className="tvup-quick-icon"><ActionIcon /></span>
                      <span>
                        <small className="tvup-quick-kicker">{action.kicker}</small>
                        <b>{action.label}</b>
                      </span>
                    </button>
                  );
                })}
              </div>
            </article>

            <article className="tvup-account-card">
              <div className="tvup-account-summary">
                <button
                  type="button"
                  className="tvup-profile-avatar"
                  aria-label="Hesabı düzenle"
                  aria-haspopup="dialog"
                  aria-controls="tvup-account-modal"
                  disabled={account.isRestricted}
                  title={account.isRestricted ? RESTRICTED_ACTION_MESSAGE : 'Hesabı düzenle'}
                  onClick={account.isRestricted ? undefined : () => openAccountModal('personal')}
                >
                  <span className="tvup-profile-avatar-initial">{user.initial}</span>
                  <i className={account.isActive ? 'is-active' : ''} aria-hidden="true" />
                  <span className="tvup-profile-avatar-edit" aria-hidden="true">
                    <EditRoundedIcon />
                  </span>
                </button>
                <div className="tvup-account-head">
                  <p className="tvup-panel-kicker"><span /> HESABIM</p>
                  <span className={`tvup-account-status is-${account.statusTone}`}>{account.statusLabel}</span>
                </div>
              </div>

              <div className="tvup-profile-body">
                <nav
                  className="tvup-account-tabs"
                  aria-label="Hesap bilgileri"
                  role="tablist"
                  style={{ '--tvup-account-tab-x': accountTabIndicatorX }}
                >
                  <button
                    type="button"
                    id="tvup-account-login-tab"
                    role="tab"
                    className={activeAccountTab === 'login' ? 'is-active' : ''}
                    aria-selected={activeAccountTab === 'login'}
                    aria-controls="tvup-account-login-panel"
                    onClick={() => setActiveAccountTab('login')}
                  >
                    <PersonOutlineRoundedIcon /><span>Giriş</span>
                  </button>
                  <button
                    type="button"
                    id="tvup-account-package-tab"
                    role="tab"
                    className={activeAccountTab === 'package' ? 'is-active' : ''}
                    aria-selected={activeAccountTab === 'package'}
                    aria-controls="tvup-account-package-panel"
                    onClick={() => setActiveAccountTab('package')}
                  >
                    <GroupRoundedIcon /><span>Paket</span>
                  </button>
                </nav>

                <div className="tvup-account-tab-viewport" aria-live="polite">
                  <div
                    className="tvup-account-tab-track"
                    style={{ transform: `translate3d(-${activeAccountTabIndex * 100}%, 0, 0)` }}
                  >
                    <section
                      id="tvup-account-login-panel"
                      className="tvup-account-slide"
                      role="tabpanel"
                      aria-labelledby="tvup-account-login-tab"
                      aria-hidden={activeAccountTab !== 'login'}
                    >
                    <div className="tvup-account-fields">
                      <div className="tvup-account-field">
                        <div>
                          <small>KULLANICI ADI</small>
                          <b>{account.username}</b>
                        </div>
                        <button
                          type="button"
                          tabIndex={activeAccountTab === 'login' ? 0 : -1}
                          aria-label="Kullanıcı adını kopyala"
                          onClick={() => copyAccountValue(account.username, 'username')}
                        >
                          {copiedAccountField === 'username' ? <CheckRoundedIcon /> : <ContentCopyRoundedIcon />}
                        </button>
                      </div>

                      <div className="tvup-account-field">
                        <div>
                          <small>ŞİFRE</small>
                          <b>{showAccountPassword && account.password ? account.password : '••••••••'}</b>
                        </div>
                        <span className="tvup-account-field-actions">
                          <button
                            type="button"
                            tabIndex={activeAccountTab === 'login' ? 0 : -1}
                            aria-label={showAccountPassword ? 'Şifreyi gizle' : 'Şifreyi göster'}
                            aria-pressed={showAccountPassword}
                            disabled={!account.password}
                            onClick={() => setShowAccountPassword((visible) => !visible)}
                          >
                            {showAccountPassword ? <VisibilityOffRoundedIcon /> : <VisibilityRoundedIcon />}
                          </button>
                          <button
                            type="button"
                            tabIndex={activeAccountTab === 'login' ? 0 : -1}
                            aria-label="Şifreyi kopyala"
                            disabled={!account.password}
                            onClick={() => copyAccountValue(account.password, 'password')}
                          >
                            {copiedAccountField === 'password' ? <CheckRoundedIcon /> : <ContentCopyRoundedIcon />}
                          </button>
                        </span>
                      </div>

                      <div className="tvup-account-url">
                        <div>
                          <small>SERVER URL</small>
                          <b>{account.serverUrl}</b>
                        </div>
                        <button
                          type="button"
                          tabIndex={activeAccountTab === 'login' ? 0 : -1}
                          aria-label="URL'yi kopyala"
                          onClick={() => copyAccountValue(account.serverUrl, 'url')}
                        >
                          {copiedAccountField === 'url' ? <CheckRoundedIcon /> : <ContentCopyRoundedIcon />}
                        </button>
                      </div>
                    </div>
                    </section>

                    <section
                      id="tvup-account-package-panel"
                      className="tvup-account-slide"
                      role="tabpanel"
                      aria-labelledby="tvup-account-package-tab"
                      aria-hidden={activeAccountTab !== 'package'}
                    >
                      <div className="tvup-package-facts">
                        <div className="tvup-package-fact">
                          <span className="tvup-package-fact-icon"><GroupRoundedIcon /></span>
                          <div>
                            <small>PAKET</small>
                            <b>{account.packageLabel}</b>
                          </div>
                        </div>
                        <div className="tvup-package-fact">
                          <span className="tvup-package-fact-icon tone-orange"><CalendarMonthRoundedIcon /></span>
                          <div>
                            <small>BİTİŞ TARİHİ</small>
                            <b>{[account.expiryLabel, account.expiryTimeLabel].filter(Boolean).join(' ')}</b>
                          </div>
                        </div>
                        <div className="tvup-package-fact">
                          <span className="tvup-package-fact-icon tone-violet"><AccessTimeRoundedIcon /></span>
                          <div>
                            <small>KALAN SÜRE</small>
                            <b>{account.remainingTimeLabel}</b>
                          </div>
                        </div>
                      </div>
                    </section>
                  </div>
                </div>

              </div>

              <span className="tvup-account-copy-status" aria-live="polite">
                {copiedAccountField ? 'Bilgi kopyalandı' : ''}
              </span>
            </article>
          </section>

            <UserPanelHomeShowcases
              series={series}
              seriesLoading={seriesLoading}
              onOpenSeries={openSeriesCategory}
              onOpenSetup={openSetupDetail}
              disabled={account.isRestricted}
            />
          </>
          )}
          </main>
        </div>
      </div>

      <UserPanelCreditModal
        open={creditModalOpen}
        onClose={() => {
          setCreditModalOpen(false);
          setCreditFlow(null);
        }}
        userInfo={userInfo}
        previewMode={creditPreview}
        isReseller={isReseller}
        resellerDiscount={resellerInfo?.discount_rate ?? 0}
        flow={creditFlow}
        provider={creditFlow?.provider || creditProvider}
        allowedPaymentOrigins={creditPaymentOrigins || LEGACY_PAYMENT_ORIGINS}
        onStartCheckout={handleStartCreditCheckout}
        onStartCryptoCheckout={handleStartCryptoCreditCheckout}
        onGetCryptoCheckoutStatus={handleGetCryptoCreditCheckoutStatus}
        onUpdateProfile={onUpdateProfile}
        onVerifyPaymentResult={onVerifyCreditPayment}
        onPaymentResult={handleCreditPaymentResult}
        onOpenWhatsapp={openCreditWhatsapp}
      />
      <UserPanelDurationModal
        open={durationModalOpen}
        onClose={() => {
          setDurationModalOpen(false);
          setModalTargetUser(null);
        }}
        userInfo={modalUserInfo}
        previewMode={durationPreview}
        onPurchase={onPurchaseDuration}
        onValidateCoupon={onValidateDurationCoupon}
        onLoadAccount={onLoadAccount}
        isReseller={isReseller}
        resellerDiscount={resellerInfo?.discount_rate ?? 0}
        onOpenCredit={(context) => {
          void openCreditModal(context);
        }}
        onSuccess={onDurationSuccess}
      />
      <UserPanelAccountModal
        open={accountModalOpen}
        onClose={() => {
          setAccountModalOpen(false);
          setModalTargetUser(null);
        }}
        userInfo={modalUserInfo}
        username={modalTargetUser?.username || account.username}
        initialTab={accountModalInitialTab}
        previewMode={false}
        onLoadAccount={onLoadAccount}
        onUpdateProfile={onUpdateProfile}
        onChangePassword={onChangePassword}
        onUpdateEmail={onUpdateEmail}
        onSendEmailCode={onSendEmailCode}
        onSendPhoneCode={onSendPhoneCode}
        onUpdatePhone={onUpdatePhone}
        onLoadTrustedDevices={onLoadTrustedDevices}
        onLoadLoginHistory={onLoadLoginHistory}
        onReportLogin={onReportLogin}
        onRemoveTrustedDevice={onRemoveTrustedDevice}
      />
      <UserPanelCategoryModal
        open={categoryModalOpen}
        onClose={() => {
          setCategoryModalOpen(false);
          setModalTargetUser(null);
        }}
        userInfo={modalUserInfo}
        username={modalTargetUser?.username || account.username}
        targetUserId={modalTargetUser?.id || null}
        previewMode={false}
        onLoadBouquets={onLoadBouquets}
        onUpdateBouquets={onUpdateBouquets}
      />
      <UserPanelAdultModal
        open={adultModalOpen}
        onClose={() => {
          setAdultModalOpen(false);
          setModalTargetUser(null);
        }}
        userInfo={modalUserInfo}
        username={modalTargetUser?.username || account.username}
        targetUserId={modalTargetUser?.id || null}
        previewMode={false}
        onLoadBouquets={onLoadBouquets}
        onUpdateBouquets={onUpdateBouquets}
      />
      <UserPanelSecurityModal
        open={securityModalOpen}
        onClose={() => {
          setSecurityModalOpen(false);
          setModalTargetUser(null);
        }}
        userInfo={modalUserInfo}
        previewMode={false}
        onFetchLogs={onFetchSecurityLogs}
      />
      <UserPanelManageModal
        open={manageModalOpen}
        onClose={() => setManageModalOpen(false)}
        userInfo={selectedSubUser ? { user_info: selectedSubUser } : userInfo}
        username={selectedSubUser?.username}
        isOwnAccount={false}
        showRemoveAction
        onOpenDuration={() => openDurationModal(selectedSubUser)}
        onOpenCategories={() => openCategoryModal(selectedSubUser)}
        onOpenAccount={() => openAccountModal('personal', selectedSubUser)}
        onOpenInstall={() => openSetup(selectedSubUser)}
        onOpenAdult={() => openAdultModal(selectedSubUser)}
        onOpenSecurity={() => openSecurityModal(selectedSubUser)}
        onRemove={async () => {
          if (selectedSubUser?.id && typeof onRemoveSubUser === 'function') {
            await onRemoveSubUser(selectedSubUser.id);
          }
          setSelectedSubUser(null);
          setManageModalOpen(false);
        }}
      />
      <UserPanelTransactionHistoryModal
        open={transactionHistoryModalOpen}
        onClose={() => setTransactionHistoryModalOpen(false)}
        userInfo={userInfo}
        previewMode={false}
        onFetchHistory={onFetchTransactionHistory}
        isReseller={isReseller}
      />
      <UserPanelMovieDetailModal
        open={movieDetailDialog.open}
        movie={movieDetailDialog.movie}
        contentType={movieDetailDialog.contentType}
        onClose={() => setMovieDetailDialog({ open: false, movie: null, contentType: 'movie' })}
        onWatch={openSelectedContentInWindows}
        onOpenComments={() => openCommentsModal(movieDetailDialog.movie, movieDetailDialog.contentType || 'movie')}
      />
      <UserPanelWindowsDownloadModal
        open={windowsDownloadModalOpen}
        onClose={() => setWindowsDownloadModalOpen(false)}
        onOpenSetup={openSetupDetail}
        content={selectedWindowsContent}
        contentType={windowsDownloadContentType}
      />
      <UserPanelCommentsModal
        open={commentsDialog.open}
        onClose={() => {
          setReviewModalOpen(false);
          setCommentsDialog({ open: false, content: null, contentType: null });
        }}
        content={commentsDialog.content}
        contentType={commentsDialog.contentType}
        onOpenReview={() => setReviewModalOpen(true)}
        onToggleHelpful={handleToggleCommentHelpful}
      />
      <UserPanelReviewModal
        open={reviewModalOpen}
        onClose={() => setReviewModalOpen(false)}
        content={commentsDialog.content}
        contentType={commentsDialog.contentType}
        onSubmit={onSubmitReview}
      />
    </div>
  );
}

export default UserPanelTemplate;
